import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Upload, Palette, MessageCircle, Save, Trash2, KeyRound, Eye, EyeOff } from "lucide-react";
import { updateShop, uploadShopLogo } from "../lib/api";
import { verifyPin, hashPin, isHashed } from "../lib/pinSecurity";

const PRESET_COLORS = [
  "#E6C662", // gold (default)
  "#D4AF37",
  "#C9A227",
  "#B8860B",
  "#9333EA",
  "#3B82F6",
  "#10B981",
  "#EF4444",
];

export default function ShopSettingsPanel({ shop }) {
  const [logoUrl, setLogoUrl] = useState(shop.logo_url || "");
  const [brandColor, setBrandColor] = useState(shop.brand_color || "#E6C662");
  const [tagline, setTagline] = useState(shop.tagline || "");
  const [waProvider, setWaProvider] = useState(shop.wa_provider || "twilio");
  const [waWebhook, setWaWebhook] = useState(shop.wa_webhook_url || "");
  const [waEnabled, setWaEnabled] = useState(!!shop.wa_enabled);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function onLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadShopLogo(shop.id, file);
      setLogoUrl(url);
      await updateShop(shop.id, { logo_url: url });
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed — did you run supabase_phase2.sql?");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function removeLogo() {
    if (!window.confirm("Remove logo?")) return;
    setLogoUrl("");
    try {
      await updateShop(shop.id, { logo_url: null });
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function saveAll() {
    setSaving(true);
    try {
      await updateShop(shop.id, {
        brand_color: brandColor,
        tagline: tagline.trim() || null,
        wa_provider: waProvider,
        wa_webhook_url: waWebhook.trim() || null,
        wa_enabled: waEnabled,
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(
        err.message?.includes("column") || err.message?.includes("does not exist")
          ? "Run supabase_phase2.sql in Supabase SQL Editor first."
          : err.message
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
      data-testid="settings-panel"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding */}
        <div className="rounded-2xl bg-charcoal border border-white/10 p-6">
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 flex items-center gap-2">
            <Palette className="w-3.5 h-3.5" strokeWidth={1.5} /> Branding
          </div>

          <div className="mt-5 space-y-5">
            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Shop Logo
              </label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-void border border-white/10 flex items-center justify-center overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt="logo" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white/30 text-xs font-numbers">N/A</span>
                  )}
                </div>
                <label className="btn-ghost text-xs inline-flex items-center gap-1.5 py-1.5 px-3 cursor-pointer">
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? "Uploading…" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onLogoChange}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                </label>
                {logoUrl && (
                  <button
                    onClick={removeLogo}
                    className="text-white/50 hover:text-red-400 p-2"
                    data-testid="logo-remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/30 mt-2">
                PNG/JPG/SVG · max 2 MB
              </div>
            </div>

            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Tagline
              </label>
              <input
                className="input-minimal"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Premium experience since 1998"
                data-testid="settings-tagline"
              />
            </div>

            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Brand Accent Color
              </label>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setBrandColor(c)}
                    data-testid={`color-${c.replace("#", "")}`}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      brandColor === c ? "border-white scale-110" : "border-white/20"
                    }`}
                    style={{ background: c }}
                    aria-label={`Brand color ${c}`}
                  />
                ))}
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="w-8 h-8 rounded-full bg-transparent border border-white/20 cursor-pointer"
                  data-testid="color-custom"
                />
                <span className="font-numbers text-xs text-white/60 tabular-nums">
                  {brandColor.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp */}
        <div className="rounded-2xl bg-charcoal border border-white/10 p-6">
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 flex items-center gap-2">
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp Notifications
          </div>

          <div className="mt-5 space-y-5">
            <div className="flex items-center justify-between rounded-xl border border-white/10 p-3">
              <div>
                <div className="text-sm">Enable on Call-Next</div>
                <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/40 mt-0.5">
                  Auto-send to customer when called
                </div>
              </div>
              <button
                onClick={() => setWaEnabled((v) => !v)}
                data-testid="wa-enable-toggle"
                className={`text-[10px] font-numbers tracking-[0.3em] uppercase rounded-full px-3 py-1.5 border ${
                  waEnabled
                    ? "border-emerald-400/50 text-emerald-400 bg-emerald-400/5"
                    : "border-white/20 text-white/40"
                }`}
              >
                {waEnabled ? "Enabled" : "Disabled"}
              </button>
            </div>

            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Provider
              </label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {["twilio", "gupshup", "generic"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setWaProvider(p)}
                    data-testid={`wa-provider-${p}`}
                    className={`text-xs font-numbers tracking-[0.2em] uppercase py-2 rounded-lg border transition-all ${
                      waProvider === p
                        ? "border-gold text-gold bg-gold/5"
                        : "border-white/15 text-white/60 hover:border-white/40"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Webhook URL
              </label>
              <input
                className="input-minimal font-numbers text-xs"
                value={waWebhook}
                onChange={(e) => setWaWebhook(e.target.value)}
                placeholder="https://your-webhook.example.com/notify"
                data-testid="wa-webhook-url"
              />
              <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/30 mt-2">
                We POST {"{provider, to, token, customer, service, shop, message}"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={saveAll}
          disabled={saving}
          data-testid="settings-save"
          className="btn-gold inline-flex items-center gap-2 text-sm py-2.5 px-5"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      <ChangePinCard shop={shop} />
    </motion.div>
  );
}

// ============================================================
// CHANGE PIN — uses PBKDF2-SHA256 (lib/pinSecurity)
// ============================================================
function ChangePinCard({ shop }) {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const status = isHashed(shop.admin_pin)
    ? { label: "Encrypted", tone: "ok" }
    : { label: "Legacy · Login once to upgrade", tone: "warn" };

  async function submit() {
    if (newPin.length < 4 || newPin.length > 8) {
      toast.error("New PIN must be 4–8 digits");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      const ok = await verifyPin(currentPin, shop.admin_pin);
      if (!ok) {
        toast.error("Current PIN is incorrect");
        return;
      }
      const hashed = await hashPin(newPin);
      await updateShop(shop.id, { admin_pin: hashed });
      toast.success("PIN updated · encrypted at rest");
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-2xl bg-charcoal border border-white/10 p-6"
      data-testid="change-pin-card"
    >
      <div className="flex items-center justify-between">
        <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 flex items-center gap-2">
          <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} /> Admin PIN · Security
        </div>
        <span
          className={`text-[10px] font-numbers tracking-[0.25em] uppercase rounded-full px-2.5 py-1 border ${
            status.tone === "ok"
              ? "border-emerald-400/40 text-emerald-400 bg-emerald-400/5"
              : "border-amber-400/40 text-amber-400 bg-amber-400/5"
          }`}
        >
          {status.label}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/50">
            Current PIN
          </label>
          <div className="relative">
            <input
              type={showCurrent ? "text" : "password"}
              inputMode="numeric"
              autoComplete="current-password"
              className="input-minimal font-numbers tracking-[0.3em] text-center"
              value={currentPin}
              onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              data-testid="pin-current"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-white/40 hover:text-gold"
              aria-label="toggle visibility"
            >
              {showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div>
          <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/50">
            New PIN (4–8 digits)
          </label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              inputMode="numeric"
              autoComplete="new-password"
              className="input-minimal font-numbers tracking-[0.3em] text-center"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              data-testid="pin-new"
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-white/40 hover:text-gold"
              aria-label="toggle visibility"
            >
              {showNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div>
          <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/50">
            Confirm New PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            className="input-minimal font-numbers tracking-[0.3em] text-center"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            data-testid="pin-confirm"
          />
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-[11px] text-white/45 max-w-md leading-relaxed">
          Stored using <span className="text-gold">PBKDF2-SHA256</span> (120k rounds, random
          salt). Forgotten PIN? Ask AIBoxHub support to reset from the Super Admin console.
        </div>
        <button
          onClick={submit}
          disabled={busy || !currentPin || !newPin || !confirmPin}
          data-testid="pin-update"
          className="btn-gold inline-flex items-center gap-2 text-sm py-2.5 px-5"
        >
          <KeyRound className="w-4 h-4" /> {busy ? "Saving…" : "Update PIN"}
        </button>
      </div>
    </div>
  );
}
